/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { CurrencyDollar, FloppyDisk, CheckCircle, XCircle, Trash, Plus, Warning, Database } from "@phosphor-icons/react";

const VAR_THRESHOLD = 15; // %
const today = () => new Date().toISOString().slice(0, 10);
const num = (n) => (Number(n) || 0);

const STATUS_STYLE = {
  draft: { bg: "#F1F5F9", fg: "#475569" },
  pending: { bg: "#FEF3C7", fg: "#92400E" },
  approved: { bg: "#DCFCE7", fg: "#166534" },
  rejected: { bg: "#FEE2E2", fg: "#991B1B" },
};
const StatusBadge = ({ s }) => {
  const st = STATUS_STYLE[s] || STATUS_STYLE.draft;
  return <span className="jde-badge" style={{ background: st.bg, color: st.fg, fontWeight: 700, fontSize: 10 }}>{(s || "").toUpperCase()}</span>;
};

const variance = (rate, po) => (po && rate ? ((num(rate) - num(po)) / num(po)) * 100 : null);

export default function TransferPricing() {
  const { user } = useAuth();
  const canApprove = ["admin", "approver"].includes(user?.role);
  const [tab, setTab] = useState("set");

  return (
    <Layout breadcrumb={["Transfer Pricing"]}>
      <div className="jde-toolbar">
        <CurrencyDollar size={14} weight="bold" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>TRANSFER PRICING (US ENTITY)</span>
        <div className="divider" />
        {[["set", "Set Rates"], ["all", "All Rates"]].map(([k, l]) => (
          <button key={k} className={`jde-btn ${tab === k ? "jde-btn-primary" : ""}`} onClick={() => setTab(k)} data-testid={`tp-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "set" && <SetRates />}
      {tab === "all" && <RateList status="" canApprove={canApprove} title="All Transfer Prices" />}
    </Layout>
  );
}

function SetRates() {
  const [customers, setCustomers] = useState([]);
  const [pcode, setPcode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [newItem, setNewItem] = useState({ icode: "", iname: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => { api.get("/ms/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);
  const custName = useMemo(() => customers.find((c) => c.ms_pcode === pcode)?.name || "", [customers, pcode]);

  const loadItems = async (pc) => {
    if (!pc) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/transfer-prices/items?pcode=${encodeURIComponent(pc)}`);
      setRows((r.data.items || []).map((it) => ({
        ...it,
        _uid: (crypto.randomUUID ? crypto.randomUUID() : `${it.icode}-${Math.random()}`),
        dirty: false,
        rate: it.existing_rate ?? it.live_rate ?? "",
        rate_wef: today(),
        valid_days: it.valid_days || 365,
        remark: it.remark || "",
      })));
    } catch (e) { toast.error("Failed to load items"); } finally { setLoading(false); }
  };
  useEffect(() => { loadItems(pcode); }, [pcode]);

  // editing ANY field marks the row dirty so ONLY user-touched rows are saved
  const setField = (uid, k, v) => setRows((rs) => rs.map((r) => r._uid === uid ? { ...r, [k]: v, dirty: true } : r));
  const addItem = () => {
    if (!newItem.icode.trim()) return toast.error("Enter an item code");
    setRows((rs) => [{ _uid: (crypto.randomUUID ? crypto.randomUUID() : `new-${Math.random()}`), dirty: true, icode: newItem.icode.trim(), iname: newItem.iname.trim(), po_rate: null, live_rate: null, rate: "", rate_wef: today(), valid_days: 365, remark: "", existing_status: null }, ...rs]);
    setNewItem({ icode: "", iname: "" });
  };

  // rows the user actually edited (dirty) are saved — the rate may be a number, 0, or blank (=NULL)
  const toSave = () => rows.filter((r) => r.dirty && (r.icode || "").toString().trim());
  // blank -> null (clears the TP price); "0" -> 0 (explicit zero); anything else -> Number
  const rateVal = (r) => {
    const s = (r.rate ?? "").toString().trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  };
  const payload = (submit) => ({
    pcode, customer_name: custName, submit,
    entries: toSave().map((r) => ({ icode: r.icode, iname: r.iname, unit: r.unit || "EA", rate: rateVal(r), rate_wef: r.rate_wef, valid_days: Number(r.valid_days) || 0, remark: r.remark, po_rate: r.po_rate })),
  });
  const save = async (submit) => {
    if (!pcode) return toast.error("Select a customer");
    if (!toSave().length) return toast.error("Edit at least one rate to save");
    setBusy(submit ? "submit" : "draft");
    try {
      const r = await api.post("/transfer-prices/bulk", payload(submit));
      toast.success(submit ? `Wrote ${r.data.applied} rate(s) to MS SQL dbo.Partyi` : `Saved ${r.data.saved} draft rate(s)`);
      setConfirmOpen(false);
      loadItems(pcode);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  return (
    <div className="jde-panel" style={{ marginTop: 12 }}>
      <div className="jde-panel-body">
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label className="jde-label">Customer</label>
            <select className="jde-select" value={pcode} onChange={(e) => setPcode(e.target.value)} data-testid="tp-customer" style={{ minWidth: 240 }}>
              <option value="">— Select customer —</option>
              {customers.map((c) => <option key={c.ms_pcode} value={c.ms_pcode}>{c.name} ({c.ms_pcode})</option>)}
            </select>
          </div>
          {pcode && <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <div><label className="jde-label">Add item code</label><input className="jde-input" value={newItem.icode} onChange={(e) => setNewItem({ ...newItem, icode: e.target.value })} placeholder="ICODE" data-testid="tp-new-icode" style={{ width: 120 }} /></div>
            <div><label className="jde-label">Description</label><input className="jde-input" value={newItem.iname} onChange={(e) => setNewItem({ ...newItem, iname: e.target.value })} placeholder="optional" data-testid="tp-new-iname" style={{ width: 160 }} /></div>
            <button className="jde-btn" onClick={addItem} data-testid="tp-add-item"><Plus size={12} weight="bold" /> Add</button>
          </div>}
        </div>

        {loading ? <div className="muted" style={{ padding: 20 }}>Loading items…</div> : pcode && (
          <>
            <div style={{ overflowX: "auto", marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid="tp-set-table">
                <thead><tr>
                  {["Item", "Description", "PO Rate", "Live Rate", "Transfer Price *", "Effective", "Valid days", "Variance", "Remark", "Status"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--jde-border)", whiteSpace: "nowrap", fontSize: 12, color: "#374151" }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const v = variance(r.rate, r.po_rate);
                    const flag = v != null && Math.abs(v) > VAR_THRESHOLD;
                    return (
                      <tr key={r._uid} data-testid={`tp-row-${r.icode}`} style={{ borderBottom: "1px solid #EEF1F4", background: r.dirty ? "#F0FDFA" : "transparent" }}>
                        <td style={{ padding: "5px 8px", fontWeight: 700, borderLeft: r.dirty ? "3px solid #0F766E" : "3px solid transparent" }} className="text-mono">{r.icode}</td>
                        <td style={{ padding: "5px 8px" }}>{r.iname}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }} className="text-mono muted">{r.po_rate ?? "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }} className="text-mono">{r.live_rate ?? "—"}</td>
                        <td style={{ padding: "5px 8px" }}><input className="jde-input text-mono" type="number" step="0.0001" value={r.rate} onChange={(e) => setField(r._uid, "rate", e.target.value)} data-testid={`tp-rate-${r.icode}`} style={{ width: 90, textAlign: "right" }} /></td>
                        <td style={{ padding: "5px 8px" }}><input className="jde-input" type="date" value={r.rate_wef} onChange={(e) => setField(r._uid, "rate_wef", e.target.value)} style={{ width: 130 }} /></td>
                        <td style={{ padding: "5px 8px" }}><input className="jde-input num" type="number" value={r.valid_days} onChange={(e) => setField(r._uid, "valid_days", e.target.value)} style={{ width: 84 }} /></td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>
                          {v == null ? <span className="muted">—</span> : (
                            <span style={{ fontWeight: 700, color: flag ? "#B91C1C" : "#166534", display: "inline-flex", gap: 3, alignItems: "center" }} data-testid={`tp-var-${r.icode}`}>
                              {flag && <Warning size={12} weight="bold" />}{v > 0 ? "+" : ""}{v.toFixed(1)}%
                            </span>)}
                        </td>
                        <td style={{ padding: "5px 8px" }}><input className="jde-input" value={r.remark} onChange={(e) => setField(r._uid, "remark", e.target.value)} placeholder="…" style={{ width: 130 }} /></td>
                        <td style={{ padding: "5px 8px" }}>{r.existing_status ? <StatusBadge s={r.existing_status} /> : <span className="muted">new</span>}</td>
                      </tr>);
                  })}
                  {!rows.length && <tr><td colSpan={10} className="muted" style={{ padding: 18, textAlign: "center" }}>No items for this customer. Add one above.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
              <span className="muted" style={{ fontSize: 11 }}>{toSave().length} edited rate(s) will be saved · only rows you edit (highlighted) are included · <strong>Write to MS SQL</strong> pushes these into <span className="text-mono">dbo.Partyi.rate</span> · variance &gt; {VAR_THRESHOLD}% vs PO is flagged</span>
              <div style={{ flex: 1 }} />
              <button className="jde-btn" onClick={() => save(false)} disabled={busy} data-testid="tp-save-draft"><FloppyDisk size={13} weight="bold" /> {busy === "draft" ? "Saving…" : "Save Draft"}</button>
              <button className="jde-btn jde-btn-primary" onClick={() => { if (!toSave().length) return toast.error("Edit at least one rate to save"); setConfirmOpen(true); }} disabled={busy} data-testid="tp-write-mssql"><Database size={13} weight="bold" /> Write to MS SQL</button>
            </div>
          </>
        )}
      </div>

      {confirmOpen && (
        <div data-testid="tp-confirm-overlay" onClick={() => !busy && setConfirmOpen(false)}
             style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="tp-confirm-modal"
               style={{ background: "white", borderRadius: 10, width: 560, maxWidth: "92vw", maxHeight: "82vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--jde-border)", display: "flex", alignItems: "center", gap: 8 }}>
              <Database size={18} weight="duotone" color="#0F766E" />
              <span style={{ fontWeight: 800, fontSize: 15 }}>Write transfer prices to MS SQL?</span>
            </div>
            <div style={{ padding: "16px 18px", overflowY: "auto" }}>
              <p style={{ fontSize: 13, color: "#334155", marginTop: 0 }}>
                This will write <strong>{toSave().length}</strong> rate(s) for <strong>{custName || pcode}</strong> directly into the live master <span className="text-mono">dbo.Partyi.rate</span> on MS SQL. Existing rows are updated; missing ones are inserted. A blank rate writes <span className="text-mono">NULL</span> (clears the price); <span className="text-mono">0</span> is stored as an explicit zero. This affects live ERP pricing immediately.
              </p>
              <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--jde-border)", borderRadius: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ background: "#F8FAFC" }}>
                    {["Item", "Live Rate (now)", "New Rate", "Var vs PO"].map((h) => (
                      <th key={h} style={{ textAlign: h === "Item" ? "left" : "right", padding: "6px 10px", borderBottom: "1px solid var(--jde-border)", fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {toSave().map((r) => {
                      const v = variance(r.rate, r.po_rate);
                      const flag = v != null && Math.abs(v) > VAR_THRESHOLD;
                      return (
                        <tr key={r._uid} style={{ borderBottom: "1px solid #EEF1F4" }}>
                          <td style={{ padding: "5px 10px", fontWeight: 700 }} className="text-mono">{r.icode}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right" }} className="text-mono muted">{r.live_rate ?? "—"}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 700, color: "#0F766E" }} className="text-mono">{(r.rate ?? "").toString().trim() === "" ? "NULL" : Number(r.rate)}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 700, color: flag ? "#B91C1C" : "#166534" }}>
                            {v == null ? "—" : <>{flag && <Warning size={11} weight="bold" style={{ marginRight: 2, verticalAlign: "-1px" }} />}{v > 0 ? "+" : ""}{v.toFixed(1)}%</>}
                          </td>
                        </tr>);
                    })}
                  </tbody>
                </table>
              </div>
              {toSave().some((r) => { const v = variance(r.rate, r.po_rate); return v != null && Math.abs(v) > VAR_THRESHOLD; }) && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 12, color: "#92400E", display: "flex", gap: 6, alignItems: "center" }}>
                  <Warning size={14} weight="bold" /> Some rates deviate more than {VAR_THRESHOLD}% from the PO rate — please double-check before writing.
                </div>
              )}
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--jde-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="jde-btn" onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="tp-confirm-cancel">Cancel</button>
              <button className="jde-btn jde-btn-primary" onClick={() => save(true)} disabled={busy} data-testid="tp-confirm-write">
                <Database size={13} weight="bold" /> {busy === "submit" ? "Writing…" : `Confirm & write ${toSave().length} rate(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RateList({ status, canApprove, title }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get(`/transfer-prices${status ? `?status=${status}` : ""}`).then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const approve = async (r) => { try { await api.post(`/transfer-prices/${r.id}/approve`); toast.success(`Approved ${r.icode} @ ${r.rate} — written to master`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); } };
  const reject = async (r) => { const reason = window.prompt(`Reject ${r.icode}? Reason (optional):`) ?? null; if (reason === null) return; try { await api.post(`/transfer-prices/${r.id}/reject`, { reason }); toast.success("Rejected"); load(); } catch (e) { toast.error("Reject failed"); } };
  const del = async (r) => { if (!window.confirm(`Delete transfer price for ${r.icode}?`)) return; try { await api.delete(`/transfer-prices/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };

  return (
    <div className="jde-panel" style={{ marginTop: 12 }}>
      <div className="jde-panel-header"><span>{title}</span><span className="muted" style={{ fontSize: 11 }}>{rows.length} rate(s)</span></div>
      <div className="jde-panel-body">
        {loading ? <div className="muted" style={{ padding: 16 }}>Loading…</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid={`tp-list-${status || "all"}`}>
              <thead><tr>
                {["Customer", "Item", "Transfer Price", "PO Rate", "Var%", "Effective", "Valid until", "Status", "By", "Actions"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--jde-border)", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>))}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`tp-item-${r.icode}`} style={{ borderBottom: "1px solid #EEF1F4", background: r.expired ? "#FFF7ED" : "transparent" }}>
                    <td style={{ padding: "5px 8px" }}>{r.customer_name || r.portal_pcode}</td>
                    <td style={{ padding: "5px 8px", fontWeight: 700 }} className="text-mono">{r.icode}<div className="muted" style={{ fontWeight: 400, fontSize: 10 }}>{r.iname}</div></td>
                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700 }} className="text-mono">{r.rate}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right" }} className="text-mono muted">{r.po_rate ?? "—"}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: r.variance_flag ? "#B91C1C" : "#166534" }}>{r.variance_pct == null ? "—" : `${r.variance_pct > 0 ? "+" : ""}${r.variance_pct}%`}{r.variance_flag && <Warning size={11} weight="bold" style={{ marginLeft: 3 }} />}</td>
                    <td style={{ padding: "5px 8px" }} className="text-mono">{r.rate_wef || "—"}</td>
                    <td style={{ padding: "5px 8px" }} className="text-mono">{r.valid_until || "—"}{r.expired && <span className="jde-badge" style={{ marginLeft: 4, background: "#FEE2E2", color: "#991B1B", fontSize: 9, fontWeight: 700 }}>EXPIRED</span>}</td>
                    <td style={{ padding: "5px 8px" }}><StatusBadge s={r.status} /></td>
                    <td style={{ padding: "5px 8px", fontSize: 12 }} className="muted">{r.status === "approved" ? r.approved_by : r.created_by}</td>
                    <td style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>
                      {r.status === "pending" && canApprove && <>
                        <button className="jde-btn jde-btn-sm" onClick={() => approve(r)} data-testid={`tp-approve-${r.icode}`} style={{ color: "#166534" }}><CheckCircle size={12} weight="bold" /> Approve</button>
                        <button className="jde-btn jde-btn-sm" onClick={() => reject(r)} data-testid={`tp-reject-${r.icode}`} style={{ color: "#991B1B", marginLeft: 4 }}><XCircle size={12} weight="bold" /> Reject</button>
                      </>}
                      {r.status !== "approved" && <button className="jde-btn jde-btn-sm" onClick={() => del(r)} data-testid={`tp-del-${r.icode}`} style={{ marginLeft: 4 }}><Trash size={11} weight="bold" /></button>}
                    </td>
                  </tr>))}
                {!rows.length && <tr><td colSpan={10} className="muted" style={{ padding: 18, textAlign: "center" }}>No transfer prices {status ? `with status "${status}"` : "yet"}.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
